import React from "react";
import { Select, ButtonGroup, NumberInput } from "@caelor/ui";
import { useNewsStore } from "../../../../../../stores/use-news-store";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  BlogElements,
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  NewsVariants,
} from "./defaults";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { t } from "i18next";
import { PaginationTypes } from "@Shared/Pagination-Types";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  DEFAULT_LABELS_COLOR,
  DEFAULT_SPACES_COLOR,
} from "../../../../../../features/news/defaults";
import { useOrgChartStore } from "../../../../../../stores/use-org-chart-store";
import { DEFAULT_ACCENT_COLOR } from "../../../../../../features/people/defaults";
import MissingAppPanel from "../../../../../../components/ui/missing-app-panel/MissingAppPanel";
import useCosmosFields from "../../../../../../hooks/use-cosmos-fields";

const VariantOptions = [
  { label: "Card", value: NewsVariants.CARD },
  { label: "List", value: NewsVariants.LIST },
];

function LayoutSettings() {
  const { data, updateConfig } = useOrgChartStore((state) => ({
    data: state.data,
    updateConfig: state.updateConfig,
  }));

  const { fields } = useCosmosFields();

  const { isCosmosInstalled, styleguideSetup } = useOpticsStore((state) => ({
    isCosmosInstalled: state.isCosmosInstalled,
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  return (
    <>
      <SettingsOptionContainer style={{ paddingBottom: 10 }}>
        <SettingsOptionHeading>{t("display-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("view-mode")}</OptionLabel>
          <OptionContent>
            <Select
              options={VariantOptions}
              value={VariantOptions.find(
                ({ value }) => value === data.config.variant
              )}
              onChange={({ value }) =>
                updateConfig({ value: { variant: value } })
              }
            />
          </OptionContent>
        </OptionContainer>

        {isCosmosInstalled ? (
          <SettingsOptionContainer style={{ paddingBottom: 10 }}>
            <OptionContainer direction="column">
              <OptionLabel>{t("subheader")}</OptionLabel>
              <OptionContent full>
                <Select
                  value={fields.find(
                    ({ value }) => value === data.config.titleFieldId
                  )}
                  options={fields}
                  onChange={(option) =>
                    updateConfig({
                      value: { titleFieldId: option?.value || "" },
                    })
                  }
                  placeholder={t("select-subheader")}
                  isClearable
                />
              </OptionContent>
            </OptionContainer>
          </SettingsOptionContainer>
        ) : (
          <OptionContainer direction="column">
            <OptionLabel>{t("show-team-profiles")}</OptionLabel>
            <MissingAppPanel
              text={t("display-cosmos-information")}
              link={
                "https://marketplace.atlassian.com/apps/1230540/cosmos-intranet-for-confluence-free?hosting=cloud&tab=overview"
              }
              linkText={t("start-trial")}
            />
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              i18n={t}
              items={CORNERS_OPTIONS}
              value={data.config.cardBorderRadius}
              options={{ input: true }}
              onClick={(value) =>
                updateConfig({
                  value: { cardBorderRadius: value },
                })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  updateConfig({
                    value: { cardBorderRadius: value },
                  });
                }
              }}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel alignToTop>{t("line-color")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              hideColorTiles
              value={{
                accent:
                  data.config.lineColor ||
                  styleguideColors?.accent ||
                  DEFAULT_ACCENT_COLOR,
              }}
              onChange={({ accent }) => {
                updateConfig({ value: { lineColor: accent } });
              }}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
}

export default LayoutSettings;
